<script setup>
import { RouterLink } from 'vue-router'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()
const currentYear = new Date().getFullYear()
</script>

<template>
    <footer class="bg-brand-surface border-t border-brand-muted/10 mt-auto">
        <div class="container mx-auto px-4 py-12">
            <!-- Main Footer Content -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">

                <!-- Brand Section -->
                <div class="col-span-1 md:col-span-1 text-center sm:text-left">
                    <RouterLink to="/"
                        class="text-2xl font-bold text-brand-text tracking-tight flex items-center justify-center sm:justify-start mb-4 group">
                        <span class="text-brand-accent group-hover:text-brand-text transition-colors">SendThe</span>
                        <span class="text-brand-text">Noise</span>
                    </RouterLink>
                    <p class="text-brand-muted text-sm mb-4 leading-relaxed">
                        {{ $t('footer.desc') }}
                    </p>
                    <div class="flex items-center justify-center sm:justify-start space-x-4">
                        <a href="https://www.threads.net/@sendthenoise" target="_blank" rel="noopener noreferrer"
                            class="hover:opacity-80 transition-opacity" :aria-label="$t('footer.follow_threads')">
                            <img src="/threads-logo.png" alt="Threads" class="w-5 h-5 dark:invert" width="20" height="20" />
                        </a>
                        <a href="https://www.instagram.com/sendthenoise" target="_blank" rel="noopener noreferrer"
                            class="hover:opacity-80 transition-opacity" :aria-label="$t('footer.follow_instagram')">
                            <svg class="w-5 h-5 text-black dark:text-white" fill="currentColor" viewBox="0 0 24 24"
                                aria-hidden="true">
                                <path fill-rule="evenodd"
                                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.488 2.53c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                                    clip-rule="evenodd" />
                            </svg>
                        </a>
                    </div>

                    <div class="mt-6">
                        <a href="https://ko-fi.com/miguelmesachica" target="_blank" rel="noopener noreferrer"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-[#FF5E5B]/10 hover:bg-[#FF5E5B]/20 text-[#C62828] text-sm font-medium rounded-full transition-colors border border-[#FF5E5B]/20">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24"
                                fill="currentColor">
                                <path
                                    d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 3.722-2.271 3.722-2.271l2.008-4.645h.919zm-5.493 3.924c-.606 1.006-2.457 1.107-2.457 1.107-6.066.307-13.685-.052-13.685-.052-1.996-.067-1.396-1.571-1.396-1.571s-.112-5.464-.099-6.953c.023-2.618 3.518-2.316 3.518-2.316l10.976.028c2.937.135 2.64 4.095 2.64 4.095-.015 3.123.504 5.662.504 5.662zM21.2 11.168l-1.353 3.167s-2.028.914-2.571.49l.646-1.78s-.686.035-1.282-.12c.002 0 .193 1.963 2.16 2.38 2.302.488 2.054-2.264 2.054-2.264l1.493-3.419h-1.147z" />
                            </svg>
                            {{ $t('adblock.support_kofi') }}
                        </a>
                    </div>
                </div>


                <!-- Explore Links -->
                <!-- Desktop View -->
                <div class="hidden sm:block">
                    <h3 class="text-brand-text font-bold mb-4">{{ $t('footer.explore') }}</h3>
                    <ul class="space-y-3">
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'album' } }"
                                class="text-brand-muted hover:text-brand-accent transition-colors duration-200 text-sm py-1 block relative group">
                                {{ $t('nav.albums') }}
                                <span
                                    class="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-accent transition-all duration-300 group-hover:w-full"></span>
                            </RouterLink>
                        </li>
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'concert' } }"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                {{ $t('nav.concerts') }}
                            </RouterLink>
                        </li>
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'festival' } }"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                {{ $t('nav.festivals') }}
                            </RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/search"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                {{ $t('nav.search') }}
                            </RouterLink>
                        </li>
                    </ul>
                </div>
                <!-- Mobile View: Accordion -->
                <details class="sm:hidden group border-b border-brand-muted/10 w-full">
                    <summary
                        class="flex justify-between items-center py-4 cursor-pointer list-none text-brand-text font-bold">
                        {{ $t('footer.explore') }}
                        <span class="transition-transform group-open:rotate-180">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd" />
                            </svg>
                        </span>
                    </summary>
                    <ul class="pb-4 space-y-3 pl-2">
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'album' } }"
                                class="text-brand-muted hover:text-brand-text text-sm block py-1">{{ $t('nav.albums') }}
                            </RouterLink>
                        </li>
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'concert' } }"
                                class="text-brand-muted hover:text-brand-text text-sm block py-1">{{ $t('nav.concerts')
                                }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink :to="{ name: 'home', query: { filter: 'festival' } }"
                                class="text-brand-muted hover:text-brand-text text-sm block py-1">{{ $t('nav.festivals')
                                }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/search" class="text-brand-muted hover:text-brand-text text-sm block py-1">
                                {{ $t('nav.search') }}</RouterLink>
                        </li>
                    </ul>
                </details>

                <!-- Account Links -->
                <!-- Desktop View -->
                <div class="hidden sm:block">
                    <h3 class="text-brand-text font-bold mb-4">{{ $t('footer.account') }}</h3>
                    <ul class="space-y-3">
                        <template v-if="userStore.isLoggedIn">
                            <li>
                                <RouterLink to="/profile"
                                    class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                    {{
                                        $t('nav.profile') }}</RouterLink>
                            </li>
                            <li>
                                <RouterLink to="/admin"
                                    class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                    {{
                                        $t('nav.settings') }}
                                </RouterLink>
                            </li>
                        </template>
                        <template v-else>
                            <li>
                                <RouterLink to="/login"
                                    class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                    {{
                                        $t('nav.login') }}
                                </RouterLink>
                            </li>
                            <li>
                                <RouterLink to="/register"
                                    class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">
                                    {{
                                        $t('nav.signup') }}
                                </RouterLink>
                            </li>
                        </template>
                    </ul>
                </div>
                <!-- Mobile View: Accordion -->
                <details class="sm:hidden group border-b border-brand-muted/10 w-full">
                    <summary
                        class="flex justify-between items-center py-4 cursor-pointer list-none text-brand-text font-bold">
                        {{ $t('footer.account') }}
                        <span class="transition-transform group-open:rotate-180">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd" />
                            </svg>
                        </span>
                    </summary>
                    <ul class="pb-4 space-y-3 pl-2">
                        <template v-if="userStore.isLoggedIn">
                            <li>
                                <RouterLink to="/profile"
                                    class="text-brand-muted hover:text-brand-text text-sm block py-1">{{
                                        $t('nav.profile') }}</RouterLink>
                            </li>
                            <li>
                                <RouterLink to="/admin"
                                    class="text-brand-muted hover:text-brand-text text-sm block py-1">{{
                                        $t('nav.settings') }}</RouterLink>
                            </li>
                        </template>
                        <template v-else>
                            <li>
                                <RouterLink to="/login"
                                    class="text-brand-muted hover:text-brand-text text-sm block py-1">{{ $t('nav.login')
                                    }}</RouterLink>
                            </li>
                            <li>
                                <RouterLink to="/register"
                                    class="text-brand-muted hover:text-brand-text text-sm block py-1">{{
                                        $t('nav.signup') }}</RouterLink>
                            </li>
                        </template>
                    </ul>
                </details>

                <!-- Legal / Info -->
                <!-- Desktop View -->
                <div
                    class="w-full sm:w-auto mt-2 sm:mt-0 border-t border-brand-muted/10 pt-6 sm:border-0 sm:pt-0 hidden sm:block">
                    <h3 class="text-brand-text font-bold mb-4">{{ $t('footer.legal') }}</h3>
                    <ul class="space-y-3">
                        <li>
                            <RouterLink to="/privacy"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">{{
                                    $t('footer.privacy') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/terms"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">{{
                                    $t('footer.terms') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/cookies"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">{{
                                    $t('footer.cookies') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/contact"
                                class="text-brand-muted hover:text-brand-accent transition-colors text-sm py-1 block">{{
                                    $t('footer.contact') }}</RouterLink>
                        </li>
                    </ul>
                </div>
                <!-- Mobile View: Accordion -->
                <details class="sm:hidden group border-b border-brand-muted/10 w-full">
                    <summary
                        class="flex justify-between items-center py-4 cursor-pointer list-none text-brand-text font-bold">
                        {{ $t('footer.legal') }}
                        <span class="transition-transform group-open:rotate-180">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd" />
                            </svg>
                        </span>
                    </summary>
                    <ul class="pb-4 space-y-3 pl-2">
                        <li>
                            <RouterLink to="/privacy" class="text-brand-muted hover:text-brand-text text-sm block py-1">
                                {{
                                    $t('footer.privacy') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/terms" class="text-brand-muted hover:text-brand-text text-sm block py-1">{{
                                $t('footer.terms') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/cookies" class="text-brand-muted hover:text-brand-text text-sm block py-1">
                                {{
                                    $t('footer.cookies') }}</RouterLink>
                        </li>
                        <li>
                            <RouterLink to="/contact" class="text-brand-muted hover:text-brand-text text-sm block py-1">
                                {{
                                    $t('footer.contact') }}</RouterLink>
                        </li>
                    </ul>
                </details>
            </div>

            <!-- Copyright -->
            <div
                class="border-t border-brand-muted/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
                <p class="text-brand-muted text-xs">
                    &copy; {{ currentYear }} SendTheNoise. {{ $t('footer.copyright') }}
                </p>
                <p class="text-brand-muted text-xs mt-2 md:mt-0 flex items-center">
                    {{ $t('footer.made_with') }} <span class="text-red-500 mx-1">&hearts;</span> {{ $t('footer.by') }}
                </p>
            </div>
        </div>
    </footer>
</template>
